import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { getAssignedTeamForOnboarding, type TeamMember } from '@/services/data/teamIntroduction';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface TeamIntroductionStepProps {
  readonly onComplete: () => void;
  readonly setLoading: (loading: boolean) => void;
}

function getInitials(firstName: string | null, lastName: string | null): string {
  const f = firstName?.charAt(0)?.toUpperCase() || '';
  const l = lastName?.charAt(0)?.toUpperCase() || '';
  return f + l || '?';
}

export function TeamIntroductionStep({ onComplete, setLoading }: TeamIntroductionStepProps) {
  const { userId } = useAuthStore();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchTeam = async () => {
      setIsLoadingTeam(true);
      const result = await getAssignedTeamForOnboarding(userId);
      if (result.success && result.data) {
        setMembers(result.data);
      }
      setIsLoadingTeam(false);
    };

    fetchTeam();
  }, [userId]);

  if (isLoadingTeam) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="h-6 w-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="text-center text-white/50 py-12 max-w-md mx-auto">
        <Users className="h-10 w-10 mx-auto mb-4 text-white/20" />
        <p className="text-sm">
          Ton équipe n'a pas encore été assignée. Elle te sera présentée très bientôt !
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto py-4">
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-8 bottom-8 w-px bg-white/10" />

        <div className="space-y-8">
          {members.map((member, index) => (
            <div key={member.role} className="relative flex gap-5 items-start">
              {/* Avatar on the timeline */}
              <div className="relative z-10 shrink-0">
                <Avatar className="h-12 w-12 border-2 border-white/20">
                  <AvatarImage src={member.avatarUrl ?? undefined} alt={member.firstName ?? ''} />
                  <AvatarFallback className="bg-white/10 text-white text-sm font-medium">
                    {getInitials(member.firstName, member.lastName)}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Content */}
              <div className="flex-1 pt-0.5">
                <p className="text-white font-semibold text-base">
                  {member.firstName ?? 'Prénom'}
                  <span className="text-white/40 font-normal text-sm ml-2">
                    — ton/ta {member.roleLabel.toLowerCase()}
                  </span>
                </p>
                <p className="text-white/50 text-sm mt-1.5 leading-relaxed">
                  {member.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
